import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { InboundFileFeed, InboundFileFeedsService, InboundSftpAccess } from '../../../services/inbound-file-feeds/inbound-file-feeds.service';

@Component({
  selector: 'app-inbound-sftp-access',
  templateUrl: './inbound-sftp-access.component.html',
  styleUrls: ['./inbound-sftp-access.component.scss'],
})
export class InboundSftpAccessComponent implements OnChanges, OnDestroy {
  @Input() feed: InboundFileFeed | null = null;
  @Input() disabled = false;
  @Output() feedUpdated = new EventEmitter<InboundFileFeed>();
  @Output() busyChanged = new EventEmitter<boolean>();
  @Output() statusChanged = new EventEmitter<{ feedId: string; status: string }>();
  access: InboundSftpAccess | null = null;
  busy = false;
  loading = false;
  error = '';
  notice = '';
  action = 'create';
  username = '';
  authMethod: 'password' | 'ssh_key' = 'ssh_key';
  secretValue = '';
  passwordVisible = false;
  publicKey = '';
  folder = '';
  replacementKeyId = '';
  revokeKeyId = '';
  replacementVerified = false;
  sessionsDrained = false;
  private generation = 0;
  private destroyed = false;

  constructor(private service: InboundFileFeedsService) {}

  ngOnChanges(): void {
    this.generation++;
    this.error = '';
    this.notice = '';
    this.access = null;
    this.loading = false;
    this.clearCredentials();
    this.username = this.feed?.transportConfig?.sftp?.username || '';
    this.folder = this.feed?.transportConfig?.sftp?.folder || '';
    if (this.feed && !this.disabled) { void this.refresh(); }
  }

  ngOnDestroy(): void { this.destroyed = true; this.generation++; this.clearCredentials(); }

  chooseAction(action: string): void {
    if (!this.canChange) { return; }
    this.action = action;
    this.actionChanged();
  }

  get submitLabel(): string {
    const labels: Record<string, string> = { create: 'Create account', link: 'Link account',
      set_auth: this.authMethod === 'password' ? 'Save password' : 'Save SSH key authentication',
      add_key: 'Add SSH key', revoke_key: 'Revoke SSH key', directory: 'Change directory' };
    return labels[this.action];
  }

  private publishStatus(status: string): void {
    if (this.feed) { this.statusChanged.emit({ feedId: this.feed.id, status }); }
  }

  get canChange(): boolean {
    return !this.disabled && !this.busy && !this.loading && !!this.feed
      && this.feed.status !== 'archived' && this.feed.transportConfig?.sftp?.enabled === true
      && !!this.access?.configured && ['ready', 'not_linked'].includes(this.access.status);
  }

  get canSubmit(): boolean {
    if (!this.canChange) { return false; }
    if (['create', 'link'].includes(this.action) && !/^[A-Za-z0-9_][A-Za-z0-9_@.-]{2,99}$/.test(this.username)) { return false; }
    if (['create', 'set_auth'].includes(this.action)) {
      return this.authMethod === 'password' ? this.secretValue.length >= 16 && this.secretValue.length <= 128 : !!this.publicKey.trim();
    }
    if (this.action === 'add_key') { return !!this.publicKey.trim(); }
    if (this.action === 'directory') { return !!this.folder.trim() && this.sessionsDrained; }
    if (this.action === 'revoke_key') {
      return !!this.revokeKeyId && !!this.replacementKeyId && this.revokeKeyId !== this.replacementKeyId && this.replacementVerified;
    }
    return this.action === 'link';
  }

  clearCredentials(): void {
    this.secretValue = '';
    this.passwordVisible = false;
    this.publicKey = '';
    this.replacementVerified = false;
    this.sessionsDrained = false;
    this.replacementKeyId = '';
    this.revokeKeyId = '';
  }

  actionChanged(): void {
    this.clearCredentials();
    this.notice = '';
  }

  async refresh(): Promise<void> {
    if (!this.feed || this.busy || this.destroyed) { return; }
    const generation = ++this.generation;
    const feedId = this.feed.id;
    this.loading = true;
    this.publishStatus('checking');
    this.access = null;
    this.error = '';
    try {
      const access = await this.service.sftpAccess(feedId);
      if (generation !== this.generation) { return; }
      this.access = access;
      this.publishStatus(access.status);
      this.action = access.account ? 'set_auth' : 'create';
      this.authMethod = access.authMethod || 'ssh_key';
      this.folder = access.account?.folder || this.feed.transportConfig?.sftp?.folder || '';
      this.actionChanged();
    } catch (_) {
      if (generation === this.generation) { this.error = 'Unable to verify SFTP access. Refresh before making changes.'; this.publishStatus('unavailable'); }
    } finally {
      if (generation === this.generation) { this.loading = false; }
    }
  }

  async submit(reconcile = false): Promise<void> {
    if (!this.feed || this.disabled || this.busy || this.loading || (!reconcile && !this.canSubmit)
      || (reconcile && !this.access?.account?.pendingAction)) { return; }
    const feedId = this.feed.id;
    const generation = this.generation;
    const payload: Record<string, unknown> = reconcile ? { action: 'reconcile' } : {
      action: this.action, username: this.username, authMethod: this.authMethod,
      secretValue: this.secretValue, publicKey: this.publicKey.trim(), folder: this.folder.trim(),
      keyId: this.revokeKeyId, replacementKeyId: this.replacementKeyId, replacementVerified: this.replacementVerified, sessionsDrained: this.sessionsDrained,
    };
    this.busy = true;
    this.busyChanged.emit(true);
    this.publishStatus('provisioning');
    this.error = '';
    this.clearCredentials();
    try {
      const access = await this.service.changeSftpAccess(feedId, payload);
      if (generation !== this.generation) { return; }
      this.access = access;
      this.publishStatus(access.status);
      this.notice = 'SFTP change verified. Existing SFTP sessions may remain open until disconnected.';
      this.action = 'set_auth';
      this.authMethod = access.authMethod || 'ssh_key';
      this.folder = access.account?.folder || '';
      this.feedUpdated.emit(await this.service.getFeed(feedId));
    } catch (error: any) {
      if (generation !== this.generation) { return; }
      this.error = typeof error?.data?.error === 'string' ? error.data.error : 'SFTP change could not be confirmed. Refresh and reconcile before retrying.';
      this.publishStatus('unavailable');
      this.access = null; // Never retain a ready state after an uncertain write.
      try {
        const access = await this.service.sftpAccess(feedId);
        if (generation === this.generation) { this.access = access; this.publishStatus(access.status); }
      } catch (_) { /* Explicit error remains visible. */ }
    } finally {
      delete payload.secretValue;
      this.busy = false;
      this.busyChanged.emit(false);
      if (generation !== this.generation && !this.disabled) { void this.refresh(); }
    }
  }

  async copyConnection(): Promise<void> {
    if (this.access?.status !== 'ready' || !this.access.account) { return; }
    try {
      await navigator.clipboard.writeText(`Host: ${this.access.host}\nPort: ${this.access.port}\nUsername: ${this.access.account.username}\nUpload directory: ${this.access.uploadPath}\nAuthentication: ${this.access.authMethod === 'password' ? 'Password' : 'SSH key'}`);
      this.notice = 'Connection details copied.';
    } catch (_) { this.error = 'Unable to copy connection details.'; }
  }
}
